import React, { useState, useRef, useEffect } from "react";

interface DualRangeSliderProps {
  min: number;
  max: number;
  step?: number;
  onChange: (values: { min: number; max: number }) => void;
  formatValue?: (value: number) => string;
}

const DualRangeSlider: React.FC<DualRangeSliderProps> = ({
  min,
  max,
  step = 1,
  onChange,
  formatValue,
}) => {
  const [minValue, setMinValue] = useState(min);
  const [maxValue, setMaxValue] = useState(max);

  const sliderRef = useRef<HTMLDivElement>(null);
  const draggingHandle = useRef<"min" | "max" | null>(null);

  const getPercent = (value: number) => ((value - min) / (max - min)) * 100;

  const handleMove = (clientX: number) => {
    if (!sliderRef.current || !draggingHandle.current) return;

    const slider = sliderRef.current.getBoundingClientRect();
    const sliderWidth = slider.width;
    const offsetX = Math.min(Math.max(clientX - slider.left, 0), sliderWidth);

    const value =
      Math.round(min + ((offsetX / sliderWidth) * (max - min)) / step) * step;

    if (draggingHandle.current === "min" && value <= maxValue) {
      setMinValue(Math.max(min, value));
    } else if (draggingHandle.current === "max" && value >= minValue) {
      setMaxValue(Math.min(max, value));
    }
  };

  const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX);
  const handleTouchMove = (e: TouchEvent) => handleMove(e.touches[0].clientX);

  const handleEnd = () => {
    draggingHandle.current = null;
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleEnd);
    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", handleEnd);
  };

  const handleStart = (handle: "min" | "max", clientX?: number) => {
    draggingHandle.current = handle;

    if (clientX !== undefined) handleMove(clientX);

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", handleEnd);
  };

  useEffect(() => {
    onChange({ min: minValue, max: maxValue });
  }, [minValue, maxValue]);

  return (
    <div className="relative w-full p-4">
      {/* Add margin below the "Precio" label */}
      <div className="mb-8">
        <span className="font-medium text-gray-700">Precio</span>
      </div>

      {/* Slider Track and Handles */}
      <div ref={sliderRef} className="relative w-full h-2 bg-gray-300 rounded">
        {/* Highlighted Range */}
        <div
          className="absolute h-full bg-primary rounded"
          style={{
            left: `${getPercent(minValue)}%`,
            right: `${100 - getPercent(maxValue)}%`,
          }}
        ></div>

        {/* Min Handle */}
        <div
          className="absolute w-4 h-4 bg-primary rounded-full cursor-pointer transform -translate-y-1/2 -translate-x-1/2"
          style={{ left: `${getPercent(minValue)}%`, top: "50%" }}
          onMouseDown={() => handleStart("min")}
          onTouchStart={(e) => handleStart("min", e.touches[0].clientX)}
        >
          <div
            className="absolute top-[-28px] bg-primary text-white text-xs font-medium px-2 py-1 rounded"
            style={{ transform: "translateX(-50%)" }}
          >
            {formatValue ? formatValue(minValue) : minValue}
          </div>
        </div>

        {/* Max Handle */}
        <div
          className="absolute w-4 h-4 bg-primary rounded-full cursor-pointer transform -translate-y-1/2 -translate-x-1/2"
          style={{ left: `${getPercent(maxValue)}%`, top: "50%" }}
          onMouseDown={() => handleStart("max")}
          onTouchStart={(e) => handleStart("max", e.touches[0].clientX)}
        >
          <div
            className="absolute top-[-28px] bg-primary text-white text-xs font-medium px-2 py-1 rounded"
            style={{ transform: "translateX(-50%)" }}
          >
            {formatValue ? formatValue(maxValue) : maxValue}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DualRangeSlider;
